import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, Calendar, Clock, Video, Phone, MapPin, Copy, Edit3, X, ArrowLeft, AlertCircle, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { api } from '../lib/api';
import { fmtDate, fmtDay, fmtTime, ymd } from '../lib/format';
import { toast } from 'sonner';

const LOCATION_ICON = { online: Video, phone: Phone, in_person: MapPin };

export default function BookConfirmPage({ mode = 'confirm' }) {
  const { token } = useParams();
  const [appt, setAppt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReschedule, setShowReschedule] = useState(false);
  const [showCancel, setShowCancel] = useState(false);

  useEffect(() => {
    api.getBooking(token).then(setAppt).catch(() => toast.error('Booking not found')).finally(() => setLoading(false));
  }, [token]);

  const refresh = () => api.getBooking(token).then(setAppt);

  if (loading) return (<><Header /><div className="py-32 text-center text-neutral-500"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div><Footer /></>);
  if (!appt) return (<><Header /><div className="py-32 text-center text-neutral-500">Booking not found.</div><Footer /></>);

  const LocIcon = LOCATION_ICON[appt.location_type] || Video;
  const isCancelled = appt.status === 'cancelled';

  return (
    <>
      <Header />
      <section className="py-16 bg-[#fbf7ee]">
        <div className="max-w-[820px] mx-auto px-6">
          {mode === 'confirm' && !isCancelled && (
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-4 shadow-[0_14px_30px_-10px_rgba(16,185,129,0.4)]">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h1 className="text-[34px] md:text-[42px] font-extrabold text-[#14110f]">You’re booked!</h1>
              <p className="mt-3 text-[15px] text-neutral-600">A confirmation has been sent to <span className="font-semibold text-[#14110f]">{appt.client_email}</span>.</p>
            </div>
          )}
          {mode === 'manage' && (
            <div className="mb-6">
              <Link to="/" className="inline-flex items-center gap-1.5 text-[13.5px] text-neutral-500 hover:text-[#c89200]"><ArrowLeft className="w-4 h-4" /> Back to wysetribe.ca</Link>
              <h1 className="mt-4 text-[34px] font-extrabold text-[#14110f]">Manage your booking</h1>
            </div>
          )}
          {isCancelled && (
            <div className="mb-6 p-4 rounded-xl bg-rose-50 border border-rose-100 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-rose-600 mt-0.5" />
              <div>
                <p className="font-semibold text-rose-800">This appointment was cancelled</p>
                <p className="text-[13px] text-rose-700 mt-0.5">If this was a mistake, please book again.</p>
              </div>
            </div>
          )}

          <div className="rounded-xl bg-white shadow-[0_25px_60px_-25px_rgba(20,17,15,0.2)] p-7">
            <div className="flex items-center justify-between">
              <div>
                <p className="section-eyebrow mb-2">Booking</p>
                <h3 className="text-[22px] font-bold text-[#14110f]">{appt.appointment_type_name}</h3>
              </div>
              {appt.advisor && (
                <div className="flex items-center gap-3">
                  <img src={appt.advisor.avatar} alt={appt.advisor.name} className="w-12 h-12 rounded-full object-cover border-[2px] border-[#f5b300]" />
                  <div className="text-right">
                    <p className="font-semibold text-[14px] text-[#14110f]">{appt.advisor.name}</p>
                    <p className="text-[12px] text-neutral-500">{appt.advisor.title}</p>
                  </div>
                </div>
              )}
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 p-4 rounded-lg bg-[#faf6ec]">
                <Calendar className="w-5 h-5 text-[#c89200] mt-0.5" />
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-neutral-500">Date</p>
                  <p className="font-semibold text-[14px] text-[#14110f]">{fmtDay(appt.start)}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-lg bg-[#faf6ec]">
                <Clock className="w-5 h-5 text-[#c89200] mt-0.5" />
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-neutral-500">Time</p>
                  <p className="font-semibold text-[14px] text-[#14110f]">{fmtTime(appt.start)} · {appt.duration_minutes} min</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-lg bg-[#faf6ec]">
                <LocIcon className="w-5 h-5 text-[#c89200] mt-0.5" />
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-neutral-500">Location</p>
                  <p className="font-semibold text-[14px] text-[#14110f] capitalize">{appt.location_type.replace('_', ' ')}</p>
                  {appt.meeting_link && <a href={appt.meeting_link} target="_blank" rel="noreferrer" className="text-[12px] text-[#c89200] underline">Join meeting</a>}
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-lg bg-[#faf6ec]">
                <CheckCircle2 className="w-5 h-5 text-[#c89200] mt-0.5" />
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-neutral-500">Status</p>
                  <p className="font-semibold text-[14px] text-[#14110f] capitalize">{appt.status}</p>
                </div>
              </div>
            </div>

            {!isCancelled && appt.status !== 'completed' && (
              <div className="mt-6 flex flex-wrap gap-3">
                <button onClick={() => setShowReschedule(true)} className="inline-flex items-center gap-2 bg-[#4a2e1a] hover:bg-[#6b3f24] text-white px-5 py-3 rounded-md font-semibold text-[14px] transition"><Edit3 className="w-4 h-4" /> Reschedule</button>
                <button onClick={() => setShowCancel(true)} className="inline-flex items-center gap-2 border border-neutral-200 hover:border-rose-400 hover:text-rose-600 text-[#14110f] px-5 py-3 rounded-md font-semibold text-[14px] transition"><X className="w-4 h-4" /> Cancel</button>
                <button onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success('Link copied'); }} className="inline-flex items-center gap-2 border border-neutral-200 hover:border-[#f5b300] text-[#14110f] px-5 py-3 rounded-md font-semibold text-[14px] transition"><Copy className="w-4 h-4" /> Copy link</button>
              </div>
            )}
          </div>

          <div className="mt-4 text-center text-[12.5px] text-neutral-500">
            Need to manage this booking later? Bookmark this page or use the link in your confirmation email.
          </div>
        </div>
      </section>
      <Footer />

      {showReschedule && <RescheduleModal appt={appt} onClose={() => setShowReschedule(false)} onDone={() => { setShowReschedule(false); refresh(); }} />}
      {showCancel && <CancelModal appt={appt} onClose={() => setShowCancel(false)} onDone={() => { setShowCancel(false); refresh(); }} />}
    </>
  );
}

function RescheduleModal({ appt, onClose, onDone }) {
  const today = new Date();
  const [view, setView] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [rangeMap, setRangeMap] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const first = new Date(view.year, view.month, 1);
    const start = ymd(first > today ? first : today);
    const days = new Date(view.year, view.month + 1, 0).getDate() + 7;
    api.availabilityRange(appt.appointment_type_id, start, days, appt.advisor_id).then(setRangeMap);
  }, [view, appt, today]);

  useEffect(() => {
    if (!selectedDate) return;
    setSlot(null);
    api.availability(appt.appointment_type_id, ymd(selectedDate), appt.advisor_id).then((d) => setSlots(d.slots || []));
  }, [selectedDate, appt]);

  const buildMatrix = () => {
    const first = new Date(view.year, view.month, 1);
    const startWd = (first.getDay() + 6) % 7;
    const dim = new Date(view.year, view.month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWd; i++) cells.push(null);
    for (let d = 1; d <= dim; d++) cells.push(new Date(view.year, view.month, d));
    while (cells.length % 7) cells.push(null);
    return cells;
  };

  const save = async () => {
    if (!slot) return;
    setSaving(true);
    try {
      await api.reschedule(appt.token, { start: slot.start, advisor_id: slot.advisor_id });
      toast.success('Appointment rescheduled');
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not reschedule'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-[22px] font-bold text-[#14110f]">Reschedule appointment</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-neutral-100 flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex items-center justify-between mb-3">
          <p className="font-semibold text-[#14110f]">{new Date(view.year, view.month, 1).toLocaleString('en-CA', { month: 'long', year: 'numeric' })}</p>
          <div className="flex gap-2">
            <button onClick={() => { const d = new Date(view.year, view.month - 1, 1); setView({ year: d.getFullYear(), month: d.getMonth() }); }} className="w-8 h-8 rounded-full border hover:bg-[#f5b300] hover:border-[#f5b300] flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => { const d = new Date(view.year, view.month + 1, 1); setView({ year: d.getFullYear(), month: d.getMonth() }); }} className="w-8 h-8 rounded-full border hover:bg-[#f5b300] hover:border-[#f5b300] flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[11px] uppercase text-neutral-400 mb-1">{['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((d) => <div key={d}>{d}</div>)}</div>
        <div className="grid grid-cols-7 gap-1">
          {buildMatrix().map((d, i) => {
            if (!d) return <div key={i} />;
            const key = ymd(d);
            const past = d < new Date(today.getFullYear(), today.getMonth(), today.getDate());
            const has = rangeMap[key]?.has_availability;
            const isSel = selectedDate && ymd(selectedDate) === key;
            return (
              <button key={key} disabled={past || !has} onClick={() => setSelectedDate(d)}
                className={`aspect-square rounded-md text-[13px] font-semibold ${
                  isSel ? 'bg-[#f5b300] text-[#14110f]'
                  : past ? 'text-neutral-300'
                  : has ? 'hover:bg-[#fff7e0] text-[#14110f]'
                  : 'text-neutral-400 line-through cursor-not-allowed'
                }`}
              >{d.getDate()}</button>
            );
          })}
        </div>
        {selectedDate && (
          <div className="mt-5">
            <p className="font-semibold text-[14px] text-[#14110f] mb-2">Available times</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {slots.length === 0 && <p className="col-span-full text-[13px] text-neutral-400 py-4">No times available.</p>}
              {slots.map((s) => {
                const sel = slot && slot.start === s.start;
                return (
                  <button key={s.start} onClick={() => setSlot(s)}
                    className={`px-2 py-2 rounded-md border text-[13px] font-semibold ${sel ? 'bg-[#f5b300] border-[#f5b300] text-[#14110f]' : 'border-neutral-200 hover:border-[#f5b300] hover:text-[#c89200]'}`}
                  >{fmtTime(s.start)}</button>
                );
              })}
            </div>
          </div>
        )}
        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onClose} className="px-5 py-3 rounded-md border border-neutral-200 hover:bg-neutral-50 text-[14px] font-semibold">Cancel</button>
          <button disabled={!slot || saving} onClick={save} className="px-5 py-3 rounded-md bg-[#f5b300] hover:bg-[#e0a200] disabled:opacity-50 text-[#14110f] text-[14px] font-semibold">{saving ? 'Saving…' : 'Confirm new time'}</button>
        </div>
      </div>
    </div>
  );
}

function CancelModal({ appt, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.cancel(appt.token, { reason }); toast.success('Appointment cancelled'); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not cancel'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-md w-full p-7" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-[20px] font-bold text-[#14110f]">Cancel this appointment?</h3>
        <p className="mt-2 text-[14px] text-neutral-600">This action cannot be undone. You can rebook anytime.</p>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Optional reason" className="mt-4 w-full px-4 py-3 rounded-md border border-neutral-200 focus:border-[#f5b300] outline-none text-[14px] resize-none" />
        <div className="mt-5 flex gap-3 justify-end">
          <button onClick={onClose} className="px-5 py-3 rounded-md border border-neutral-200 hover:bg-neutral-50 text-[14px] font-semibold">Keep booking</button>
          <button disabled={saving} onClick={save} className="px-5 py-3 rounded-md bg-rose-500 hover:bg-rose-600 disabled:opacity-50 text-white text-[14px] font-semibold">{saving ? 'Cancelling…' : 'Cancel appointment'}</button>
        </div>
      </div>
    </div>
  );
}
